import type { EndpointBuilder } from '@reduxjs/toolkit/query'

import { rootApi } from 'root-api-graphql'

const COMMERCE_BASE_URL = '/api/commerce-service/graphql'

const COMMERCE_TAG_TYPE = 'CommercePurchaseFulfillment' as const

// istanbul ignore next
const GetPurchaseFulfillmentsByEventUuidsDocument = `
  query GetPurchaseFulfillmentsByEventUuids(
    $eventUuids: [ID!]!
    $filter: PurchaseFulfillmentsByEventUuidsFilterInput
  ) {
    purchaseFulfillmentsByEventUuids(eventUuids: $eventUuids, filter: $filter) {
      collection {
        uuid
        purchasedPackageUuid
        fulfilledItemType
        fulfilledItemUuid
        purchasedPackage {
          uuid
          packageUuid
          name
          packageType
          ownerType
          ownerUuid
          contactUuid
          purchasedPackageItems {
            uuid
            itemType
            itemUuid
            numUnits
            unitType
            fulfilledUnits
            remainingUnits
          }
        }
        package {
          uuid
          name
          status
          packageType
        }
      }
      pagination {
        count
        nextPageToken
        previousPageToken
      }
    }
  }
`

export interface PurchasedPackageItem {
  uuid: string
  itemType: string
  itemUuid: string
  numUnits: number
  unitType: string
  fulfilledUnits: number
  remainingUnits: number
}

export interface PurchasedPackage {
  uuid: string
  packageUuid: string
  name: string
  packageType: string
  ownerType: string
  ownerUuid: string
  contactUuid: string | null
  purchasedPackageItems: PurchasedPackageItem[]
}

export interface Package {
  uuid: string
  name: string
  status: string
  packageType: string
}

export interface PurchaseFulfillment {
  uuid: string
  purchasedPackageUuid: string
  fulfilledItemType: string
  fulfilledItemUuid: string
  purchasedPackage: PurchasedPackage
  package: Package
}

export interface PurchaseFulfillmentsResult {
  purchaseFulfillmentsByEventUuids: {
    collection: PurchaseFulfillment[]
    pagination: {
      count: number
      nextPageToken: string | null
      previousPageToken: string | null
    }
  }
}

export interface GetPurchaseFulfillmentsArgs {
  eventUuids: string[]
  ownerUuid: string
}

/* istanbul ignore next -- RTK Query endpoint; tested via hook integration */
const commerceGraphqlApi = rootApi
  .enhanceEndpoints({ addTagTypes: [COMMERCE_TAG_TYPE] })
  .injectEndpoints({
    endpoints: (build: EndpointBuilder<any, any, any>) => ({
      getPurchaseFulfillmentsByEventUuids: build.query<PurchaseFulfillmentsResult, GetPurchaseFulfillmentsArgs>({
        query: ({ eventUuids, ownerUuid }: GetPurchaseFulfillmentsArgs) => ({
          document: GetPurchaseFulfillmentsByEventUuidsDocument,
          variables: {
            eventUuids,
            filter: {
              ownerUuid,
              count: eventUuids.length,
              pageToken: null,
            },
          },
          meta: { baseUrl: COMMERCE_BASE_URL },
        }),
        providesTags: [COMMERCE_TAG_TYPE],
      }),
    }),
  })

export const {
  useGetPurchaseFulfillmentsByEventUuidsQuery,
} = commerceGraphqlApi
